/**
 * Screenshot utility functions for Replate viewer
 *
 * NOTE: This module is ported from the legacy PlayCanvas viewer.
 * It may need further adaptation for the Spark unified viewer.
 */

export interface ScreenshotOptions {
  /**
   * Final square size (default: 1024)
   */
  size?: number;

  /**
   * Resize mode: "contain" | "cover" | "stretch"
   */
  mode?: "contain" | "cover" | "stretch";

  /**
   * Open screenshots in new tabs (default: false)
   */
  open?: boolean;

  /**
   * Disable skybox for transparent background (default: true)
   */
  disableSkyboxForTransparent?: boolean;

  /**
   * Upload token for backend API
   */
  token?: string;

  /**
   * VITE_DOMAIN for API calls
   */
  VITE_DOMAIN?: string;

  /**
   * Asset UUID for upload
   */
  asset?: string;
}

export interface ScreenshotResult {
  bg: string;
  dataUrl: string;
  base64: string;
}

/**
 * Handle screenshot capture with multiple backgrounds
 *
 * @param opts Screenshot options
 * @returns Promise resolving to screenshot results or single result
 */
export const handleScreenshot = async (opts: ScreenshotOptions = {}): Promise<ScreenshotResult | ScreenshotResult[]> => {
  const {
    size = 1024,
    mode = "contain",
    open = false,
    disableSkyboxForTransparent = true,
    token,
    VITE_DOMAIN,
    asset: uuid
  } = opts;

  console.log('Screenshot options:', opts);

  try {
    const container = document.getElementById("replate-viewer");
    if (!container) throw new Error("Viewer container not found");

    // Get canvas from shadow DOM or regular DOM
    let canvas: HTMLCanvasElement | null = null;
    if ((container as any).shadowRoot) {
      canvas = (container as any).shadowRoot.querySelector("canvas");
    } else {
      canvas = container.querySelector("canvas");
    }
    if (!canvas) throw new Error("Canvas not found");

    // Debug sizes
    console.log({
      css: [canvas.clientWidth, canvas.clientHeight],
      bitmap: [canvas.width, canvas.height],
      dpr: window.devicePixelRatio
    });

    // Build list of backgrounds to output
    const backgrounds = ["transparent", "black", "white"];
    const results: ScreenshotResult[] = [];

    // Note: Skybox and camera manipulation would need to be implemented
    // for the specific viewer implementation (PlayCanvas vs Spark)
    // This is a placeholder for the unified viewer
    const prevSkybox = (window as any).app?.scene?.skybox;

    for (const bg of backgrounds) {
      // TODO: Implement setCameraClearColor for Spark viewer
      // For now, just capture current state
      if ((window as any).app && (window as any).camera) {
        if (bg === "transparent" && disableSkyboxForTransparent && (window as any).app.scene) {
          (window as any).app.scene.skybox = null;
        } else if ((window as any).app.scene) {
          (window as any).app.scene.skybox = prevSkybox ?? (window as any).app.scene.skybox;
        }
        setCameraClearColor((window as any).camera, bg);
        await renderOnce((window as any).app);
      }

      // Capture original
      const original = canvas.toDataURL("image/png");

      // Resize to exact square
      const dataUrl = await resizeDataURL(original, size, mode, "transparent");
      const base64 = dataUrl.split(",")[1];

      if (bg === "transparent") {
        console.log("Transparent dataUrl length:", dataUrl.length);

        // Debug download link
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "debug-transparent.png";
        a.innerText = "Download debug transparent PNG";
        document.body.appendChild(a);
      }

      // Open in new tab (optional)
      if (open) openInNewTab(dataUrl, size, bg);

      results.push({ bg: bg || '', dataUrl, base64: base64 || '' });
    }

    // Upload if token and UUID provided
    if (results.length > 0 && token && uuid && VITE_DOMAIN) {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("uuid", uuid);

      // Convert base64 → Blob for each image
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (!result) continue;
        const blob = await (await fetch(result.dataUrl)).blob();
        formData.append("files", blob, `${result.bg}.png`);
      }

      const uploadUrl = `https://api.${VITE_DOMAIN}/models/v1/thumbnails/upload`;
      console.log("Uploading thumbnails to:", uploadUrl);

      const uploadRes = await fetch(uploadUrl, {
        method: "POST",
        body: formData
      });

      const uploadJson = await uploadRes.json();
      console.log("Upload result:", uploadJson);

      if (!uploadRes.ok) throw new Error(uploadJson.error || "Upload failed");

      if (window.ReactNativeWebView) {
        console.log("Screenshot upload successful, notifying React Native app.");
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: "SCREENSHOT_RESULT",
          ok: true
        }));
      }

      window.postMessage(JSON.stringify({ type: "SCREENSHOT_RESULT", ok: true }), '*');
    }

    return results.length === 1 ? results[0]! : results;
  } catch (err) {
    console.error("handleScreenshot error:", (err as Error)?.message || err);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "SCREENSHOT_RESULT",
        ok: false
      }));
    }

    window.postMessage(JSON.stringify({ type: "SCREENSHOT_RESULT", ok: false }), '*');
    throw err;
  }
};

/**
 * Set camera clear color for different backgrounds
 * NOTE: This is for PlayCanvas camera API - needs adaptation for Spark
 */
function setCameraClearColor(camera: any, bg: string): void {
  if (!camera?.clearColor) return;
  if (bg === "transparent") camera.clearColor.set(0, 0, 0, 0);
  else if (bg === "black") camera.clearColor.set(0, 0, 0, 1);
  else if (bg === "white") camera.clearColor.set(1, 1, 1, 1);
  camera.clearColorBuffer = true;
}

/**
 * Render a single frame (PlayCanvas API)
 * NOTE: Needs adaptation for Spark viewer
 */
function renderOnce(app: any): Promise<void> {
  return new Promise(resolve => {
    if (!app) return resolve();
    app.once("postrender", resolve);
    app.renderNextFrame = true;
  });
}

/**
 * Resize a data URL to a square with specified background
 */
function resizeDataURL(
  dataUrl: string,
  size: number = 1024,
  mode: "contain" | "cover" | "stretch" = "contain",
  bg: string = "transparent"
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const out = document.createElement("canvas");
      out.width = size;
      out.height = size;
      const ctx = out.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get 2D context"));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      if (bg !== "transparent") {
        ctx.fillStyle = bg === "black" ? "#000" : "#fff";
        ctx.fillRect(0, 0, size, size);
      }

      const iw = img.width;
      const ih = img.height;

      if (mode === "stretch") {
        ctx.drawImage(img, 0, 0, size, size);
      } else if (mode === "contain") {
        const s = Math.min(size / iw, size / ih);
        const w = Math.round(iw * s);
        const h = Math.round(ih * s);
        const x = Math.round((size - w) / 2);
        const y = Math.round((size - h) / 2);
        ctx.drawImage(img, x, y, w, h);
      } else { // "cover"
        const s = Math.max(size / iw, size / ih);
        const w = Math.round(iw * s);
        const h = Math.round(ih * s);
        const x = Math.round((size - w) / 2);
        const y = Math.round((size - h) / 2);
        ctx.drawImage(img, x, y, w, h);
      }

      resolve(out.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Open screenshot in a clean tab without overflow
 */
function openInNewTab(dataUrl: string, size: number, bg?: string): void {
  const w = window.open("");
  if (!w) return; // popup blocked
  w.document.write(`
    <!doctype html><meta name="viewport" content="width=device-width,initial-scale=1">
    <style>
      html,body{margin:0;background:#111}
      img{display:block;max-width:100vw;max-height:100vh;object-fit:contain;margin:auto}
    </style>
    <img src="${dataUrl}" width="${size}" height="${size}" alt="screenshot ${bg || ''}"/>
  `);
}

// Expose globally for backward compatibility
(window as any).handleScreenshot = handleScreenshot;
